/* ==========================================================================
   Custom Field Helpers (PORTAL-147)

   Shared, pure helpers for the prescriber-entered "Additional Information"
   values on a catalog product. Used by the product subform to gate the submit
   button and to build the submit/draft payloads, so the button, the inline
   `register()` rules in PrescriptionDetails and the backend validator
   (backend/app/utils/custom_fields.py) all agree on what "filled in" means.
   ========================================================================== */

import type { CatalogProductForPortal, CustomFieldDefinition } from '@/api/types'

/**
 * A custom field value the form treats as "not filled in".
 *
 * Note the NaN case: an emptied `<input type="number">` registered with
 * `valueAsNumber` reads back as NaN, not '', so it slips past a plain
 * `value !== ''` check and then serializes to JSON `null`.
 */
export function isBlankCustomFieldValue(value: unknown): boolean {
  if (value === undefined || value === null || value === '') return true
  return typeof value === 'number' && Number.isNaN(value)
}

/**
 * Whether a required custom field still needs an answer.
 *
 * Deliberately mirrors the inline `register(...)` rules in CustomFieldsEdit, so
 * the gate is never STRICTER than what the prescriber can satisfy on screen
 * (which would leave Submit disabled with nothing to fix) and never LOOSER than
 * what the backend accepts — the latter is what lost the prescriber's work in
 * the first place.
 *
 * - checkbox: must be checked. The backend would also accept `false`
 *   (`False == ""` is False in Python), but the inline rule requires `true`, and
 *   a required attestation only means something once it is ticked. Matching the
 *   stricter of the two keeps the button and the inline error in sync.
 * - text: NOT trimmed. The inline `required` rule accepts whitespace and so does
 *   the backend; trimming here would disable Submit with no error to fix.
 * - number: `0` is a real answer; only blank/NaN counts as missing.
 * - a field this portal cannot render an input for (a dropdown stored without
 *   options, or an unknown type — both fall through to `return null` in
 *   CustomFieldsEdit) is never gated: there would be no way to satisfy it. The
 *   backend still rejects it, which is the pre-existing behavior.
 */
export function isRequiredCustomFieldMissing(
  field: CustomFieldDefinition,
  value: unknown,
): boolean {
  if (!field.required) return false
  switch (field.type) {
    case 'checkbox':
      return !value
    case 'dropdown':
      return field.options && field.options.length > 0
        ? isBlankCustomFieldValue(value)
        : false
    case 'text':
    case 'number':
    case 'date':
      return isBlankCustomFieldValue(value)
    default:
      return false
  }
}

/** Required fields still missing a value, in catalog order (labels for messaging). */
export function getMissingRequiredCustomFields(
  fields: CustomFieldDefinition[] | null | undefined,
  values: Record<string, unknown> | null | undefined,
): CustomFieldDefinition[] {
  if (!fields || fields.length === 0) return []
  return fields.filter((field) =>
    isRequiredCustomFieldMissing(field, values?.[field.key]),
  )
}

/**
 * A blank value for every custom field of `product`, used to clear the section
 * when the prescriber picks a different product.
 *
 * Resetting to `{}` is not enough: react-hook-form's setValue walks the keys of
 * the object it is given, so an empty object updates the stored form value but
 * never touches the DOM. The text/number/date inputs are uncontrolled and React
 * reuses the input node when the new product happens to reuse a field key, so
 * the previous product's text would stay on screen over an empty form value.
 *
 * Blank is '' for every type (including checkbox) so the payload filters drop it
 * exactly as they already do for a field that was never touched — blanking a
 * checkbox to `false` would start sending "No" for optional checkboxes that used
 * to be omitted entirely.
 */
export function blankCustomFieldValues(
  product: CatalogProductForPortal | null,
): Record<string, string> {
  return blankCustomFieldValuesFor(product?.custom_fields)
}

/** Same as blankCustomFieldValues, for a bare definition list (general fields). */
export function blankCustomFieldValuesFor(
  fields: CustomFieldDefinition[] | null | undefined,
): Record<string, string> {
  const blank: Record<string, string> = {}
  for (const field of fields ?? []) blank[field.key] = ''
  return blank
}

/**
 * Only the values whose key is still a defined field. A restored draft or a
 * prescribe-again seed can carry a key the pharmacy has since deleted or
 * renamed; the form renders no input for it, and the backend would reject it
 * as an unknown key.
 */
export function pickDefinedCustomFieldValues(
  fields: CustomFieldDefinition[] | null | undefined,
  values: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  const picked: Record<string, unknown> = {}
  for (const field of fields ?? []) {
    if (values && field.key in values) picked[field.key] = values[field.key]
  }
  return picked
}

/**
 * Submitted values with blanks dropped, or null when nothing was filled in —
 * the shape both submit and draft payloads carry.
 */
export function compactCustomFieldValues(
  values: Record<string, unknown> | null | undefined,
): Record<string, string | number | boolean> | null {
  if (!values) return null
  const filtered: Record<string, string | number | boolean> = {}
  for (const [key, value] of Object.entries(values)) {
    if (!isBlankCustomFieldValue(value)) filtered[key] = value as string | number | boolean
  }
  return Object.keys(filtered).length > 0 ? filtered : null
}

/**
 * Reasons the current definitions cannot be saved, in display order. Hosts
 * use this to block Save (the editor only paints the problems inline).
 *
 * `knownProviderIds` (PORTAL-181, general fields only): when given, a field
 * whose `provider_ids` names an id outside the set is reported too, so Save
 * never hits the backend's 422 blindly. The backend strips deleted providers
 * from every assignment, so this only fires when another admin deleted a
 * provider after this page loaded. Omit it while the provider list is loading
 * or failed to load.
 */
export function getCustomFieldDefinitionProblems(
  fields: CustomFieldDefinition[],
  knownProviderIds?: Set<string>,
): string[] {
  const problems: string[] = []
  const keys = fields.map((f) => f.key).filter(Boolean)
  if (keys.length !== new Set(keys).size) {
    problems.push('Duplicate field keys detected. Each field must have a unique key.')
  }
  fields.forEach((field, index) => {
    if (!field.label.trim()) {
      problems.push(`Field ${index + 1} needs a label.`)
    } else if (!field.key.trim()) {
      // Keys are slugified from the label; a label with no leading letter
      // (e.g. "123") produces an empty key.
      problems.push(`"${field.label.trim()}" needs a label that starts with a letter.`)
    }
    if (
      field.type === 'dropdown' &&
      (!field.options || field.options.length === 0 || field.options.every((o) => !o.trim()))
    ) {
      problems.push(`"${field.label.trim() || `Field ${index + 1}`}" is a dropdown with no options.`)
    }
    if (knownProviderIds && field.provider_ids?.some((id) => !knownProviderIds.has(id))) {
      problems.push(
        `"${field.label.trim() || `Field ${index + 1}`}" is assigned to a provider that no longer exists. Remove them to save.`,
      )
    }
  })
  return problems
}
